/**
 * 割り勘計算（純粋関数・I/Oなし）
 *
 * - 固定額の人／グループを先に差し引き
 * - 残額を「余り人数」で Math.ceil して均等割り（全員同額）
 */

export type PersonInput = {
  kind: "person";
  /** 表示名 */
  name: string;
  /** 指定時は固定額。未指定・null なら余り割り対象 */
  fixedAmount?: number | null;
};

export type GroupInput = {
  kind: "group";
  /** グループ名（例: 女性） */
  name: string;
  /** 人数（1以上） */
  count: number;
  /** 1人あたり固定額 */
  amountEach: number;
};

export type Participant = PersonInput | GroupInput;

export type SplitInput = {
  /** 合計金額（円・0以上の整数） */
  total: number;
  participants: Participant[];
  /**
   * 名前なしの余り人数（任意）。
   * 固定額なしの個人と合算して余り割り対象になる。
   */
  extraRemainderCount?: number;
};

export type LineItem = {
  label: string;
  count: number;
  amountEach: number;
  subtotal: number;
  role: "fixed" | "remainder";
};

export type SplitOk = {
  ok: true;
  total: number;
  fixedSubtotal: number;
  leftover: number;
  remainderCount: number;
  remainderEach: number;
  /** 余り側の徴収合計（ceil のため leftover 以上になり得る） */
  remainderCollected: number;
  lines: LineItem[];
  /** 全員の支払い合計 */
  collected: number;
  /** collected - total（切り上げによる超過分） */
  overage: number;
};

export type SplitErr = {
  ok: false;
  error: string;
};

export type SplitResult = SplitOk | SplitErr;

function isNonNegInt(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= 0;
}

function isPosInt(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= 1;
}

/**
 * 固定額＋余り切り上げ割り勘を計算する。
 */
export function calculateWarikan(input: SplitInput): SplitResult {
  if (!input || typeof input !== "object") {
    return { ok: false, error: "入力が不正です" };
  }

  const { total, participants } = input;
  const extraRemainderCount = input.extraRemainderCount ?? 0;

  if (!Number.isFinite(total) || !isNonNegInt(total)) {
    return { ok: false, error: "合計金額は0以上の整数で入力してください" };
  }

  if (!Array.isArray(participants)) {
    return { ok: false, error: "参加者リストが不正です" };
  }

  if (!isNonNegInt(extraRemainderCount)) {
    return { ok: false, error: "追加の余り人数は0以上の整数で入力してください" };
  }

  const lines: LineItem[] = [];
  let fixedSubtotal = 0;
  const remainderNames: string[] = [];

  for (let i = 0; i < participants.length; i++) {
    const p = participants[i];
    if (!p || typeof p !== "object" || !("kind" in p)) {
      return { ok: false, error: `参加者 #${i + 1} の形式が不正です` };
    }

    if (p.kind === "person") {
      const name = (p.name ?? "").trim();
      if (!name) {
        return { ok: false, error: `参加者 #${i + 1} の名前を入力してください` };
      }
      const fixed = p.fixedAmount;
      if (fixed === undefined || fixed === null) {
        remainderNames.push(name);
        continue;
      }
      if (!Number.isFinite(fixed) || !isNonNegInt(fixed)) {
        return {
          ok: false,
          error: `${name} の固定額は0以上の整数で入力してください`,
        };
      }
      fixedSubtotal += fixed;
      lines.push({
        label: name,
        count: 1,
        amountEach: fixed,
        subtotal: fixed,
        role: "fixed",
      });
      continue;
    }

    if (p.kind === "group") {
      const name = (p.name ?? "").trim();
      if (!name) {
        return { ok: false, error: `グループ #${i + 1} の名前を入力してください` };
      }
      if (!isPosInt(p.count)) {
        return {
          ok: false,
          error: `グループ「${name}」の人数は1以上の整数で入力してください`,
        };
      }
      if (!Number.isFinite(p.amountEach) || !isNonNegInt(p.amountEach)) {
        return {
          ok: false,
          error: `グループ「${name}」の1人あたり金額は0以上の整数で入力してください`,
        };
      }
      const subtotal = p.count * p.amountEach;
      fixedSubtotal += subtotal;
      lines.push({
        label: `${name}（${p.count}人）`,
        count: p.count,
        amountEach: p.amountEach,
        subtotal,
        role: "fixed",
      });
      continue;
    }

    return { ok: false, error: `参加者 #${i + 1} の種類が不明です` };
  }

  if (fixedSubtotal > total) {
    return {
      ok: false,
      error: `固定額の合計（${fixedSubtotal.toLocaleString("ja-JP")}円）が合計金額（${total.toLocaleString("ja-JP")}円）を超えています`,
    };
  }

  const leftover = total - fixedSubtotal;
  const remainderCount = remainderNames.length + extraRemainderCount;

  if (remainderCount === 0 && leftover > 0) {
    return {
      ok: false,
      error: `余り ${leftover.toLocaleString("ja-JP")}円 がありますが、余りを割る人がいません。余り人数を追加するか、固定額を見直してください`,
    };
  }

  let remainderEach = 0;
  if (remainderCount > 0) {
    remainderEach = Math.ceil(leftover / remainderCount);
  }

  const remainderCollected = remainderEach * remainderCount;

  for (const name of remainderNames) {
    lines.push({
      label: name,
      count: 1,
      amountEach: remainderEach,
      subtotal: remainderEach,
      role: "remainder",
    });
  }

  if (extraRemainderCount > 0) {
    lines.push({
      label:
        extraRemainderCount === 1
          ? "その他"
          : `その他（${extraRemainderCount}人）`,
      count: extraRemainderCount,
      amountEach: remainderEach,
      subtotal: remainderCollected - remainderEach * remainderNames.length,
      role: "remainder",
    });
  }

  // 余り0人かつ余り0円（全員固定でぴったり）のケース
  const collected = fixedSubtotal + remainderCollected;
  const overage = collected - total;

  return {
    ok: true,
    total,
    fixedSubtotal,
    leftover,
    remainderCount,
    remainderEach,
    remainderCollected,
    lines,
    collected,
    overage,
  };
}

/** 円表示（例: 1,500円） */
export function formatYen(n: number): string {
  return `${n.toLocaleString("ja-JP")}円`;
}
